import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchTestHistory } from '../../utils/testApi';

function TestHistory() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const averageScoreByTheme = useMemo(() => {
    if (history.length === 0) {
      return [];
    }

    const grouped = history.reduce((acc, item) => {
      const themeName = item.theme_name || '不明なテーマ';
      if (!acc[themeName]) {
        acc[themeName] = { themeName, totalScore: 0, count: 0 };
      }

      acc[themeName].totalScore += Number(item.score) || 0;
      acc[themeName].count += 1;
      return acc;
    }, {});

    return Object.values(grouped)
      .map((theme) => ({
        ...theme,
        averageScore: (theme.totalScore / theme.count).toFixed(1),
      }))
      .sort((a, b) => b.count - a.count);
  }, [history]);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        setLoading(true);
        setError('');
        const result = await fetchTestHistory({ limit: 50, offset: 0 });
        setHistory(result.data?.history || []);
      } catch (err) {
        setError(err.message || '回答履歴の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, []);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Webテスト回答履歴</h1>
        <button
          type="button"
          onClick={() => navigate('/tests')}
          className="px-4 py-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200"
        >
          テスト一覧
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center min-h-[240px]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      ) : history.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-500">回答履歴がありません</p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-2">
            <p className="text-sm text-blue-900 font-semibold">過去50問のテーマ別平均点</p>
            <div className="flex flex-wrap gap-2">
              {averageScoreByTheme.map((theme) => (
                <div key={theme.themeName} className="px-3 py-2 rounded bg-white border border-blue-100">
                  <p className="text-xs text-blue-700 font-medium">{theme.themeName}</p>
                  <p className="text-sm text-blue-900">平均: {theme.averageScore}点</p>
                  <p className="text-xs text-blue-600">{theme.count}件</p>
                </div>
              ))}
            </div>
          </div>

          {history.map((item) => (
            <div key={item.answer_id} className="bg-white rounded-lg shadow p-4 border border-gray-100">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-gray-900">{item.theme_name}</p>
                <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">score: {item.score}</span>
                <span className={`text-xs px-2 py-1 rounded ${item.correct ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {item.correct ? 'correct' : 'incorrect'}
                </span>
              </div>
              <p className="text-sm text-gray-800 mt-2 whitespace-pre-wrap">Q. {item.question_text}</p>
              <p className="text-sm text-blue-800 mt-2 whitespace-pre-wrap">模範解答: {item.model_answer}</p>
              <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">A. {item.answer_text}</p>
              <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">feedback: {item.feedback}</p>
              <p className="text-xs text-gray-500 mt-2">{new Date(item.created_at).toLocaleString('ja-JP')}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TestHistory;
